import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  flowMax,
} from 'ad-hok'
import { FC } from 'react'
import { addApi } from 'shared/src/api'
import { createUseStyles } from 'react-jss'
import {
  DataLoadingState,
  hasData,
  isLoading,
  makeDataErrorCold,
  makeDataLoaded,
  makeDataLoadingCold,
} from 'shared/src/utils/dataLoading'
import { ProductInput, ProductSuggestion } from 'shared/src/api/types'
import { typedAs } from 'shared/src/utils/typedAs'
import {
  addOptionalIntQueryParam,
  addOptionalQueryParam,
} from 'shared/src/utils/addNavigate'
import { addEffectOnMount } from 'ad-hok-utils'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { FormProps } from './resource'
import { NumberField, TextField } from './TextField'
import { ResourceSelectField } from './ResourceSelectField'
import { ImageField } from './ImageField'
import { Button } from './Button'

export interface FormValues
  extends Omit<Partial<ProductInput>, 'price' | 'relatedProductIds'> {
  price?: string
  imageId?: number
  relatedProductId1?: number
  relatedProductId2?: number
  relatedProductId3?: number
}

const useStyles = createUseStyles({
  urlSection: {
    display: 'flex',
    justifyContent: 'stretch',
    alignItems: 'end',
  },
  button: {
    marginBottom: 32,
    marginLeft: 12,
    marginRight: 0,
  },
})

export const ProductForm: FC<FormProps<FormValues>> = flowMax(
  addDisplayName('ProductForm'),
  addOptionalIntQueryParam('productUrlId'),
  addOptionalQueryParam('url'),
  addEffectOnMount(({ productUrlId, url, values, onChange }) => () => {
    if (productUrlId || url) {
      onChange({ ...values, productUrlId, url })
    }
  }),
  addState('isEditingNestedForm', 'setEditingNestedForm', false),
  addTranslationHelpers,
  addApi,
  addState(
    'loadingProductSuggestion',
    'setLoadingProductSuggestion',
    typedAs<DataLoadingState<ProductSuggestion> | null>(null)
  ),
  addProps(({ loadingProductSuggestion }) => ({
    productSuggestion:
      loadingProductSuggestion && hasData(loadingProductSuggestion)
        ? loadingProductSuggestion.data
        : null,
  })),
  addHandlers({
    showSuggestionError:
      ({ setLoadingProductSuggestion }) =>
      (message: string) => {
        setLoadingProductSuggestion(makeDataErrorCold(new Error(message)))
        alert(message)
      },
  }),
  addHandlers({
    getProductSuggestion:
      ({
        values,
        api,
        setLoadingProductSuggestion,
        showSuggestionError,
        onChange,
        t,
      }) =>
      async () => {
        if (!values.url) {
          return
        }

        setLoadingProductSuggestion(makeDataLoadingCold())

        try {
          const suggestion = await api.products.suggestionFromProductUrl(
            values.url
          )

          if (suggestion) {
            setLoadingProductSuggestion(makeDataLoaded(suggestion))

            onChange({
              ...values,
              name: suggestion.name ?? undefined,
              brandId: suggestion.brand?.id ?? undefined,
              price: suggestion.price
                ? (suggestion.price / 100).toString()
                : undefined,
              imageId: undefined,
            })
          } else {
            showSuggestionError(
              t('admin.resources.general.suggestion.errorSuggestionUnavailable')
            )
          }
        } catch (err) {
          showSuggestionError(
            t('admin.resources.general.suggestion.errorSuggestionGeneric')
          )
        }
      },
    clearImageSuggestion:
      ({ productSuggestion, setLoadingProductSuggestion }) =>
      () => {
        if (productSuggestion) {
          const { image, ...suggestionWithoutImage } = productSuggestion
          setLoadingProductSuggestion(
            makeDataLoaded({
              ...suggestionWithoutImage,
              image: null,
            })
          )
        }
      },
  }),
  addProps(({ loadingProductSuggestion }) => ({
    isLoadingProductSuggestion:
      loadingProductSuggestion !== null && isLoading(loadingProductSuggestion),
  })),
  addProps(({ values, isLoadingProductSuggestion, t }) => ({
    classes: useStyles(),
    loadSuggestionTitle: isLoadingProductSuggestion
      ? t('admin.resources.general.suggestion.loadingSuggestion')
      : t('admin.resources.general.suggestion.loadFromUrl'),
    loadSuggestionDisabled:
      !values.url || values.url?.length === 0 || isLoadingProductSuggestion,
  })),
  ({
    values,
    onChange,
    errors,
    t,
    productSuggestion,
    getProductSuggestion,
    loadSuggestionTitle,
    loadSuggestionDisabled,
    clearImageSuggestion,
    classes,
  }) => (
    <div>
      <div className={classes.urlSection}>
        <TextField
          id="url"
          label={t('admin.resources.products.fields.url')}
          value={values.url}
          onChange={(url) => onChange({ ...values, url })}
          error={errors.url}
          focusOnMount
        />
        <Button
          text={loadSuggestionTitle}
          onClick={getProductSuggestion}
          disabled={loadSuggestionDisabled}
          className={classes.button}
        />
      </div>
      <ResourceSelectField
        id="brandId"
        label={t('admin.resources.products.fields.brand')}
        error={errors.brandId}
        resourceName="brands"
        selectedId={values.brandId}
        onChange={(brandId) => onChange({ ...values, brandId })}
        suggestion={productSuggestion?.brand?.id}
      />
      <TextField
        id="category"
        label={t('admin.resources.products.fields.category')}
        value={values.category}
        onChange={(category) => onChange({ ...values, category })}
        error={errors.category}
      />
      <TextField
        id="name"
        label={t('admin.resources.products.fields.name')}
        value={values.name}
        onChange={(name) => onChange({ ...values, name })}
        error={errors.name}
        suggestion={productSuggestion?.name}
      />
      <NumberField
        id="price"
        label={t('admin.resources.products.fields.price')}
        value={values.price}
        onChange={(price) => onChange({ ...values, price })}
        error={errors.price}
        suggestion={productSuggestion?.price}
      />
      <ImageField
        id="imageId"
        label={t('admin.resources.products.fields.imageId')}
        value={values.imageId}
        onChange={(imageId) => {
          onChange({ ...values, imageId })
          clearImageSuggestion()
        }}
        error={errors.imageId}
        suggestion={productSuggestion?.image}
      />
      <ResourceSelectField
        id="relatedProduct1"
        label={t('admin.resources.products.fields.relatedProducts')}
        error={errors.relatedProductId1}
        resourceName="products"
        selectedId={values.relatedProductId1}
        onChange={(relatedProductId1) =>
          onChange({ ...values, relatedProductId1 })
        }
      />
      <ResourceSelectField
        id="relatedProduct2"
        error={errors.relatedProductId2}
        resourceName="products"
        selectedId={values.relatedProductId2}
        onChange={(relatedProductId2) =>
          onChange({ ...values, relatedProductId2 })
        }
      />
      <ResourceSelectField
        id="relatedProduct3"
        error={errors.relatedProductId3}
        resourceName="products"
        selectedId={values.relatedProductId3}
        onChange={(relatedProductId3) =>
          onChange({ ...values, relatedProductId3 })
        }
      />
    </div>
  )
)
